import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgClickOutsideDirective } from 'ng-click-outside2';

let nextPanelId = 0;

export interface SortDropdownOption {
  value: string;
  title: string;
  icon: string;
}

export interface SortDropdownGroup {
  label: string;
  options: SortDropdownOption[];
}

export interface BuildSortGroupsConfig {
  /** Sort field for the name/subject group. Pass empty string to omit. */
  nameField?: string;
  /** Group heading for the name group. Default: 'Name'. */
  nameLabel?: string;
  /** Title for ascending option. Default: 'A → Z'. */
  nameAscTitle?: string;
  /** Title for descending option. Default: 'Z → A'. */
  nameDescTitle?: string;
  /** Sort field for the "date created" group (e.g. 'createdAt'). Omit to skip. */
  createdField?: string;
  /** Sort field for the "date modified" group (e.g. 'modifiedAt'). Omit to skip. */
  modifiedField?: string;
}

/**
 * Build the standard three-section sort group list (Name / Date created / Date modified).
 * Omit any field by leaving it undefined.
 */
export function buildSortGroups(config: BuildSortGroupsConfig): SortDropdownGroup[] {
  const groups: SortDropdownGroup[] = [];

  if (config.nameField) {
    groups.push({
      label: config.nameLabel ?? 'Name',
      options: [
        {
          value: `${config.nameField},asc`,
          title: config.nameAscTitle ?? 'A → Z',
          icon: 'sort_by_alpha',
        },
        {
          value: `${config.nameField},desc`,
          title: config.nameDescTitle ?? 'Z → A',
          icon: 'sort_by_alpha',
        },
      ],
    });
  }

  if (config.createdField) {
    groups.push({
      label: 'Date created',
      options: [
        {
          value: `${config.createdField},desc`,
          title: 'Newest first',
          icon: 'schedule',
        },
        {
          value: `${config.createdField},asc`,
          title: 'Oldest first',
          icon: 'history',
        },
      ],
    });
  }

  if (config.modifiedField) {
    groups.push({
      label: 'Date modified',
      options: [
        {
          value: `${config.modifiedField},desc`,
          title: 'Newest first',
          icon: 'edit_note',
        },
        {
          value: `${config.modifiedField},asc`,
          title: 'Oldest first',
          icon: 'draw',
        },
      ],
    });
  }

  return groups;
}

@Component({
  selector: 'app-sort-dropdown',
  standalone: true,
  imports: [NgClickOutsideDirective],
  templateUrl: './sort-dropdown.component.html',
  styleUrl: './sort-dropdown.component.css',
})
export class SortDropdownComponent {
  @Input({ required: true }) groups: SortDropdownGroup[] = [];
  @Input({ required: true }) selectedValue: string = '';
  @Input() label: string = 'Sort by';
  @Input() triggerIcon: string = 'swap_vert';
  @Input() panelAlign: 'left' | 'right' = 'left';
  @Input() panelWidth: string = '280px';

  @Output() selectionChange = new EventEmitter<string>();

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  isOpen = false;
  readonly panelId = `sort-menu-${++nextPanelId}`;

  get selectedTitle(): string {
    for (const group of this.groups) {
      const match = group.options.find((o) => o.value === this.selectedValue);
      if (match) return match.title;
    }
    return '';
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Opens and moves focus to the chosen option (else the first), as a menu button does. */
  open() {
    this.isOpen = true;
    setTimeout(() => {
      const items = this.items();
      (items.find((item) => item.getAttribute('aria-checked') === 'true') ?? items[0])?.focus();
    });
  }

  /** `returnFocus` hands focus back to the trigger (keyboard close, selection). */
  close(returnFocus = false) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (returnFocus) this.trigger?.nativeElement.focus();
  }

  select(value: string) {
    this.selectionChange.emit(value);
    this.close(true);
  }

  openFromKeyboard(event: Event) {
    event.preventDefault();
    if (!this.isOpen) this.open();
  }

  /** Arrows, Home and End move between options; Tab closes back to the trigger. */
  onPanelKeydown(event: KeyboardEvent) {
    const items = this.items();
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index < 0 ? items.length - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(items.length - 1);
        break;
      case 'Tab':
        event.preventDefault();
        this.close(true);
        break;
    }
  }

  private items(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('.sortPanel [role="menuitemradio"]')
    );
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (!this.isOpen) return;
    this.close(this.host.nativeElement.contains(document.activeElement));
  }
}
